
import React from 'react';
import { Button } from '@/components/ui/button';
import { Menu, UserPlus, Star } from 'lucide-react';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { useAuth } from '@/contexts/AuthContext';
import { Link, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { useTranslation } from "react-i18next";
import { useSidebar } from "@/components/ui/sidebar";
import { useLanguage } from "@/contexts/LanguageContext";
import { useIsActiveAffiliate } from '@/hooks/useIsActiveAffiliate';

// Language items for the dropdown
const LANGUAGES = [
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "vi", label: "Tiếng Việt", flag: "🇻🇳" },
  { code: "zh", label: "中文", flag: "🇨🇳" },
  { code: "th", label: "ไทย", flag: "🇹🇭" },
  { code: "ko", label: "한국어", flag: "🇰🇷" },
  { code: "ja", label: "日本語", flag: "🇯🇵" }
];

export const MobileTopbar = () => {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const { t } = useTranslation();
  const { toggleSidebar } = useSidebar();
  const { language, setLanguage } = useLanguage();
  const { hasAffiliateRecord } = useIsActiveAffiliate();

  const handleLogout = async () => {
    try {
      await signOut();
      toast.success(t('logout'));
    } catch (error) {
      console.error("Error during logout:", error);
      toast.error(t('logout') + " failed");
    }
  };

  // Menu items for the dropdown
  const baseMenuItems = [
    { label: t('dashboard'), path: '/' },
    { label: t('my_bots'), path: '/my-bots' },
    { label: t('premium_bots'), path: '/marketplace' },
    { label: t('prop_trading_bots'), path: '/prop-trading' },
    { label: t('exchange_accounts'), path: '/exchange-accounts' },
    { label: t('build_your_bot_with_ai'), path: '/community' },
  ];

  const affiliateMenuItem = hasAffiliateRecord 
    ? { label: t('affiliate_program'), path: '/affiliate' }
    : { label: t('become_a_partner'), path: '/affiliate/onboarding' };

  const menuItems = [
    ...baseMenuItems,
    affiliateMenuItem,
    { label: t('settings'), path: '/settings' },
  ];

  return (
    <header className="h-14 flex items-center justify-between px-4 border-b border-border bg-background" style={{ paddingTop: 'env(safe-area-inset-top)', height: 'calc(3.5rem + env(safe-area-inset-top))' }}>
      <Button 
        variant="ghost" 
        size="icon" 
        className="h-9 w-9" 
        onClick={toggleSidebar}
        aria-label="Toggle sidebar">
        <Menu size={20} />
      </Button>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="relative h-9 w-9 rounded-full">
            <Avatar className="h-9 w-9">
              <AvatarFallback className="bg-primary/20 text-primary-foreground">
                {user?.user_metadata?.name?.split(' ')
                  .map(n => n[0])
                  .join('')
                  .toUpperCase() || 'TB'}
              </AvatarFallback>
            </Avatar>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-56" align="end" forceMount>
          <DropdownMenuLabel>{t("my_account")}</DropdownMenuLabel>
          
          {/* Language Selection */}
          <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
            {t("change_language")}
          </DropdownMenuLabel>
          <div className="px-2 py-1.5">
            <div className="grid grid-cols-2 gap-1">
              {LANGUAGES.map((lang) => (
                <Button
                  key={lang.code}
                  variant="ghost"
                  size="sm"
                  className={`justify-start h-8 ${language === lang.code ? 'bg-accent text-accent-foreground font-semibold' : ''}`}
                  onClick={() => setLanguage(lang.code)}
                >
                  <span className="mr-2 text-base">{lang.flag}</span>
                  <span className="text-xs">{lang.label}</span>
                </Button>
              ))}
            </div>
          </div>
          
          <DropdownMenuSeparator />
          
          {/* Menu Navigation */}
          {menuItems.map((item) => (
            <DropdownMenuItem key={item.path} asChild>
              <Link 
                to={item.path}
                className={location.pathname === item.path ? 'bg-accent' : ''}
              >
                {item.label}
              </Link>
            </DropdownMenuItem>
          ))}
          
          <DropdownMenuSeparator />
          
          <DropdownMenuItem onClick={handleLogout} className="text-red-500 cursor-pointer">
            {t("log_out")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
};
